import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Drawer,
    Form,
    Input,
    List,
    Modal,
    Row,
    Select,
    Space,
    Tag,
    Timeline,
    Tooltip,
    Typography,
    Upload,
    message
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    DownloadOutlined,
    PaperClipOutlined,
    PlusOutlined,
    ReloadOutlined,
    RollbackOutlined,
    SendOutlined,
    SwapOutlined,
    UploadOutlined,
    UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import Layout from './Layout';
import './DocWorkflowKanbanPage.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const REGISTRY_TYPE_LABELS = {
    customer_order: 'Заказ покупателя',
    supplier_order: 'Заказ поставщика',
    customer_act: 'Акт покупателя',
    supplier_act: 'Акт поставщика'
};

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Низкий' },
    { value: 'normal', label: 'Обычный' },
    { value: 'high', label: 'Высокий' }
];

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const currentUserId = () => {
    try {
        const user = JSON.parse(
            localStorage.getItem('user') || '{}'
        );
        return Number(user.id) || null;
    } catch (_) {
        return null;
    }
};

const money = value =>
    Number(value || 0).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const DocWorkflowKanbanPage = () => {
    const [loading, setLoading] = useState(false);
    const [stages, setStages] = useState([]);
    const [users, setUsers] = useState([]);
    const [summary, setSummary] = useState({
        awaiting_me: 0,
        my_created: 0,
        active_total: 0
    });
    const [mineOnly, setMineOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState(null);

    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm] = Form.useForm();
    const [registryOptions, setRegistryOptions] = useState([]);
    const [registrySearching, setRegistrySearching] =
        useState(false);

    const [actionOpen, setActionOpen] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [actionForm] = Form.useForm();
    const [actionLoading, setActionLoading] = useState(false);
    const [createFiles, setCreateFiles] = useState([]);
    const [detailUploading, setDetailUploading] =
        useState(false);

    const me = currentUserId();

    const userOptions = useMemo(
        () =>
            users.map(user => ({
                value: user.id,
                label: user.name || user.login
            })),
        [users]
    );

    const loadBoard = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (mineOnly) params.mine = 1;
            if (search.trim()) params.q = search.trim();
            if (typeFilter) params.registry_type = typeFilter;

            const [boardRes, summaryRes] = await Promise.all([
                axios.get('/api/doc-workflows/board', {
                    headers: authHeaders(),
                    params
                }),
                axios.get('/api/doc-workflows/summary', {
                    headers: authHeaders()
                })
            ]);

            setStages(boardRes.data?.data?.stages || []);
            setSummary(
                summaryRes.data?.data || {
                    awaiting_me: 0,
                    my_created: 0,
                    active_total: 0
                }
            );
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось загрузить канбан согласования'
            );
        } finally {
            setLoading(false);
        }
    }, [mineOnly, search, typeFilter]);

    const loadUsers = useCallback(async () => {
        try {
            const res = await axios.get(
                '/api/doc-workflows/users',
                { headers: authHeaders() }
            );
            setUsers(res.data?.data || []);
        } catch (_) {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    useEffect(() => {
        loadBoard();
    }, [loadBoard]);

    useEffect(() => {
        const params = new URLSearchParams(
            window.location.search
        );
        const registryId = params.get('registry_id');

        if (!registryId) {
            return;
        }

        createForm.setFieldsValue({
            registry_id: Number(registryId),
            priority: 'normal',
            submit: true
        });
        setCreateOpen(true);
        searchRegistries(String(registryId));
        window.history.replaceState(
            {},
            document.title,
            window.location.pathname
        );
    }, [createForm]);

    const openDetail = async id => {
        setSelectedId(id);
        setDetailLoading(true);
        try {
            const res = await axios.get(
                `/api/doc-workflows/${id}`,
                { headers: authHeaders() }
            );
            setDetail(res.data?.data || null);
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось открыть карточку'
            );
        } finally {
            setDetailLoading(false);
        }
    };

    const searchRegistries = async value => {
        setRegistrySearching(true);
        try {
            const res = await axios.get(
                '/api/doc-workflows/registries',
                {
                    headers: authHeaders(),
                    params: { q: value || '' }
                }
            );
            setRegistryOptions(
                (res.data?.data || []).map(item => ({
                    value: item.id,
                    label: `№${item.number} · ${
                        REGISTRY_TYPE_LABELS[item.type] ||
                        item.type
                    } · ${item.counterparty_name} · ${money(
                        item.total_amount
                    )} ₽`
                }))
            );
        } catch (_) {
            setRegistryOptions([]);
        } finally {
            setRegistrySearching(false);
        }
    };

    const openCreate = () => {
        createForm.resetFields();
        createForm.setFieldsValue({
            priority: 'normal',
            submit: true
        });
        setCreateFiles([]);
        setCreateOpen(true);
        searchRegistries('');
    };

    const handleCreate = async () => {
        try {
            const values = await createForm.validateFields();

            if (
                !values.registry_id &&
                createFiles.length === 0
            ) {
                message.warning(
                    'Выберите документ из реестра или загрузите свой файл'
                );
                return;
            }

            const formData = new FormData();
            if (values.registry_id) {
                formData.append(
                    'registry_id',
                    values.registry_id
                );
            }
            formData.append(
                'assignee_id',
                values.assignee_id
            );
            if (values.title) {
                formData.append('title', values.title);
            }
            if (values.comment) {
                formData.append('comment', values.comment);
            }
            formData.append(
                'priority',
                values.priority || 'normal'
            );
            formData.append('submit', 'true');

            createFiles.forEach(file => {
                formData.append(
                    'files',
                    file.originFileObj || file
                );
            });

            await axios.post('/api/doc-workflows', formData, {
                headers: {
                    ...authHeaders(),
                    'Content-Type': 'multipart/form-data'
                }
            });
            message.success('Карточка согласования создана');
            setCreateOpen(false);
            setCreateFiles([]);
            loadBoard();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error ||
                    'Ошибка создания'
            );
        }
    };

    const uploadDetailFiles = async fileList => {
        if (!selectedId || !fileList?.length) {
            return;
        }

        setDetailUploading(true);
        try {
            const formData = new FormData();
            fileList.forEach(file => {
                formData.append(
                    'files',
                    file.originFileObj || file
                );
            });

            const res = await axios.post(
                `/api/doc-workflows/${selectedId}/files`,
                formData,
                {
                    headers: {
                        ...authHeaders(),
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setDetail(res.data?.data || null);
            message.success('Файлы загружены');
            loadBoard();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось загрузить файлы'
            );
        } finally {
            setDetailUploading(false);
        }
    };

    const downloadFile = async file => {
        try {
            const res = await axios.get(
                `/api/doc-workflows/${selectedId}/files/${file.id}/download`,
                {
                    headers: authHeaders(),
                    responseType: 'blob'
                }
            );

            const url = window.URL.createObjectURL(
                new Blob([res.data])
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                file.original_name || 'file'
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось скачать файл'
            );
        }
    };

    const deleteFile = async file => {
        try {
            const res = await axios.delete(
                `/api/doc-workflows/${selectedId}/files/${file.id}`,
                { headers: authHeaders() }
            );
            setDetail(res.data?.data || null);
            message.success('Файл удалён');
            loadBoard();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось удалить файл'
            );
        }
    };

    const openAction = (type, card = detail) => {
        if (!card) return;
        setSelectedId(card.id);
        setActionType(type);
        actionForm.resetFields();
        setActionOpen(true);

        if (!detail || detail.id !== card.id) {
            openDetail(card.id);
        }
    };

    const runAction = async () => {
        if (!selectedId || !actionType) return;

        try {
            const values = await actionForm.validateFields();
            setActionLoading(true);

            const urls = {
                submit: `/api/doc-workflows/${selectedId}/submit`,
                approve: `/api/doc-workflows/${selectedId}/approve`,
                reject: `/api/doc-workflows/${selectedId}/reject`,
                return: `/api/doc-workflows/${selectedId}/return`,
                reassign: `/api/doc-workflows/${selectedId}/reassign`
            };

            await axios.post(
                urls[actionType],
                values,
                { headers: authHeaders() }
            );

            const labels = {
                submit: 'Отправлено на согласование',
                approve: 'Согласовано',
                reject: 'Отклонено',
                return: 'Возвращено на доработку',
                reassign: 'Согласующий изменён'
            };

            message.success(labels[actionType] || 'Готово');
            setActionOpen(false);
            await loadBoard();
            if (selectedId) {
                await openDetail(selectedId);
            }
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error ||
                    'Не удалось выполнить действие'
            );
        } finally {
            setActionLoading(false);
        }
    };

    const cardActions = card => {
        const stageCode = card.stage?.code;
        const isMine =
            Number(card.current_assignee_id) === me;
        const isCreator = Number(card.created_by) === me;
        const roleId = (() => {
            try {
                return Number(
                    JSON.parse(
                        localStorage.getItem('user') || '{}'
                    ).role_id
                );
            } catch (_) {
                return null;
            }
        })();
        const isAdmin = roleId === 1;

        const buttons = [];

        if (
            (stageCode === 'draft' ||
                stageCode === 'revision') &&
            (isCreator || isAdmin)
        ) {
            buttons.push(
                <Button
                    key="submit"
                    size="small"
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={event => {
                        event.stopPropagation();
                        openAction('submit', card);
                    }}
                >
                    Отправить
                </Button>
            );
        }

        if (
            (stageCode === 'specialist' ||
                stageCode === 'manager') &&
            (isMine || isAdmin)
        ) {
            buttons.push(
                <Button
                    key="approve"
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={event => {
                        event.stopPropagation();
                        openAction('approve', card);
                    }}
                >
                    Согласовать
                </Button>
            );
            buttons.push(
                <Button
                    key="return"
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={event => {
                        event.stopPropagation();
                        openAction('return', card);
                    }}
                >
                    На доработку
                </Button>
            );
            buttons.push(
                <Button
                    key="reject"
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    onClick={event => {
                        event.stopPropagation();
                        openAction('reject', card);
                    }}
                >
                    Отклонить
                </Button>
            );
        }

        return buttons;
    };

    const actionModalTitle = {
        submit: 'Отправить на согласование',
        approve: 'Согласовать',
        reject: 'Отклонить документ',
        return: 'Вернуть на доработку',
        reassign: 'Сменить согласующего'
    };

    const needsNextAssignee =
        actionType === 'submit' ||
        (actionType === 'approve' &&
            detail?.stage?.code === 'specialist') ||
        actionType === 'reassign';

    const needsComment =
        actionType === 'reject' ||
        actionType === 'return';

    return (
        <Layout>
            <div className="dwf-page">
                <div className="dwf-hero">
                    <div>
                        <Title level={2}>
                            Согласование документов
                        </Title>
                        <Paragraph>
                            Канбан документооборота: автор
                            отправляет документ специалисту,
                            затем руководителю. Кнопки
                            «Согласовать» видит только
                            текущий назначенный согласующий.
                        </Paragraph>
                    </div>

                    <div className="dwf-stats">
                        <div className="dwf-stat">
                            <b>{summary.awaiting_me}</b>
                            <span>ждут меня</span>
                        </div>
                        <div className="dwf-stat">
                            <b>{summary.my_created}</b>
                            <span>мои заявки</span>
                        </div>
                        <div className="dwf-stat">
                            <b>{summary.active_total}</b>
                            <span>в работе</span>
                        </div>
                    </div>
                </div>

                <Card className="dwf-toolbar" size="small">
                    <Row gutter={[12, 12]} align="middle">
                        <Col flex="auto">
                            <Input.Search
                                allowClear
                                placeholder="Поиск по номеру, названию, контрагенту"
                                onSearch={value =>
                                    setSearch(value)
                                }
                                enterButton
                            />
                        </Col>
                        <Col>
                            <Select
                                allowClear
                                placeholder="Тип документа"
                                style={{ width: 200 }}
                                value={typeFilter}
                                onChange={setTypeFilter}
                                options={Object.entries(
                                    REGISTRY_TYPE_LABELS
                                ).map(([value, label]) => ({
                                    value,
                                    label
                                }))}
                            />
                        </Col>
                        <Col>
                            <Button
                                type={
                                    mineOnly
                                        ? 'primary'
                                        : 'default'
                                }
                                icon={<UserOutlined />}
                                onClick={() =>
                                    setMineOnly(prev => !prev)
                                }
                            >
                                На мне
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadBoard}
                                loading={loading}
                            >
                                Обновить
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openCreate}
                            >
                                Новое согласование
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <div className="dwf-board">
                    {stages.map(stage => (
                        <div
                            className="dwf-column"
                            key={stage.id}
                        >
                            <div className="dwf-column-head">
                                <div className="dwf-column-title">
                                    <span
                                        className="dwf-dot"
                                        style={{
                                            background:
                                                stage.color
                                        }}
                                    />
                                    {stage.name}
                                </div>
                                <span className="dwf-count">
                                    {(stage.cards || []).length}
                                </span>
                            </div>
                            <div className="dwf-column-body">
                                {(stage.cards || []).length ===
                                0 ? (
                                    <div className="dwf-empty">
                                        Нет карточек
                                    </div>
                                ) : (
                                    stage.cards.map(card => {
                                        const isMine =
                                            Number(
                                                card.current_assignee_id
                                            ) === me;

                                        return (
                                            <div
                                                key={card.id}
                                                className={`dwf-card${
                                                    isMine
                                                        ? ' is-mine'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    openDetail(
                                                        card.id
                                                    )
                                                }
                                            >
                                                <div className="dwf-card-top">
                                                    <Tag color="blue">
                                                        {card.registry_number
                                                            ? `№${card.registry_number}`
                                                            : `ID ${card.id}`}
                                                    </Tag>
                                                    {card.priority ===
                                                        'high' && (
                                                        <Tag color="red">
                                                            Срочно
                                                        </Tag>
                                                    )}
                                                </div>
                                                <div className="dwf-card-title">
                                                    {card.title ||
                                                        card.registry_title ||
                                                        'Без названия'}
                                                </div>
                                                <div className="dwf-card-meta">
                                                    <div>
                                                        {card.registry_id
                                                            ? REGISTRY_TYPE_LABELS[
                                                                  card.registry_type
                                                              ] ||
                                                              card.registry_type ||
                                                              'Документ реестра'
                                                            : 'Свои файлы'}
                                                    </div>
                                                    {card.counterparty_name && (
                                                        <div>
                                                            {
                                                                card.counterparty_name
                                                            }
                                                        </div>
                                                    )}
                                                    {card.registry_id ? (
                                                        <div>
                                                            {money(
                                                                card.total_amount
                                                            )}{' '}
                                                            ₽
                                                        </div>
                                                    ) : null}
                                                    {(card.files_count >
                                                        0 ||
                                                        card.files
                                                            ?.length >
                                                            0) && (
                                                        <div>
                                                            <PaperClipOutlined />{' '}
                                                            {card.files_count ||
                                                                card.files
                                                                    ?.length ||
                                                                0}{' '}
                                                            файл(ов)
                                                        </div>
                                                    )}
                                                    <div>
                                                        <UserOutlined />{' '}
                                                        {card.assignee_name ||
                                                            'не назначен'}
                                                    </div>
                                                </div>
                                                <div
                                                    className="dwf-card-actions"
                                                    onClick={event =>
                                                        event.stopPropagation()
                                                    }
                                                >
                                                    {cardActions(
                                                        {
                                                            ...card,
                                                            stage
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Drawer
                width={560}
                open={Boolean(selectedId)}
                onClose={() => {
                    setSelectedId(null);
                    setDetail(null);
                }}
                title={
                    detail
                        ? detail.title ||
                          `Согласование #${detail.id}`
                        : 'Карточка'
                }
                extra={
                    detail ? (
                        <Space wrap>
                            {cardActions(detail)}
                            <Tooltip title="Сменить согласующего">
                                <Button
                                    icon={<SwapOutlined />}
                                    onClick={() =>
                                        openAction(
                                            'reassign',
                                            detail
                                        )
                                    }
                                />
                            </Tooltip>
                        </Space>
                    ) : null
                }
            >
                {detailLoading && (
                    <Alert
                        type="info"
                        message="Загрузка…"
                        showIcon
                    />
                )}

                {!detailLoading && detail && (
                    <Space
                        direction="vertical"
                        size={16}
                        style={{ width: '100%' }}
                    >
                        <Card size="small">
                            <Space direction="vertical" size={6}>
                                <Text type="secondary">
                                    Документ
                                </Text>
                                {detail.registry_id ? (
                                    <>
                                        <Text strong>
                                            №
                                            {
                                                detail.registry_number
                                            }{' '}
                                            ·{' '}
                                            {REGISTRY_TYPE_LABELS[
                                                detail.registry_type
                                            ] ||
                                                detail.registry_type}
                                        </Text>
                                        <Text>
                                            {
                                                detail.counterparty_name
                                            }
                                        </Text>
                                        <Text>
                                            Сумма:{' '}
                                            {money(
                                                detail.total_amount
                                            )}{' '}
                                            ₽
                                        </Text>
                                        <a
                                            href={`/docs?id=${detail.registry_id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Открыть в Документы
                                            2.0
                                        </a>
                                    </>
                                ) : (
                                    <Text strong>
                                        Согласование своих
                                        файлов (без реестра)
                                    </Text>
                                )}
                            </Space>
                        </Card>

                        <Card
                            size="small"
                            title="Вложения"
                            extra={
                                <Upload
                                    multiple
                                    showUploadList={false}
                                    beforeUpload={() => false}
                                    onChange={({ fileList }) => {
                                        const ready =
                                            fileList.filter(
                                                f =>
                                                    f.originFileObj
                                            );
                                        if (ready.length) {
                                            uploadDetailFiles(
                                                ready
                                            );
                                        }
                                    }}
                                >
                                    <Button
                                        size="small"
                                        icon={
                                            <UploadOutlined />
                                        }
                                        loading={
                                            detailUploading
                                        }
                                    >
                                        Загрузить
                                    </Button>
                                </Upload>
                            }
                        >
                            {(detail.files || []).length ===
                            0 ? (
                                <Text type="secondary">
                                    Файлы ещё не загружены
                                </Text>
                            ) : (
                                <List
                                    size="small"
                                    dataSource={detail.files}
                                    renderItem={file => (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    key="dl"
                                                    type="link"
                                                    size="small"
                                                    icon={
                                                        <DownloadOutlined />
                                                    }
                                                    onClick={() =>
                                                        downloadFile(
                                                            file
                                                        )
                                                    }
                                                >
                                                    Скачать
                                                </Button>,
                                                <Button
                                                    key="rm"
                                                    type="link"
                                                    size="small"
                                                    danger
                                                    icon={
                                                        <DeleteOutlined />
                                                    }
                                                    onClick={() =>
                                                        deleteFile(
                                                            file
                                                        )
                                                    }
                                                >
                                                    Удалить
                                                </Button>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <PaperClipOutlined />
                                                }
                                                title={
                                                    file.original_name
                                                }
                                                description={
                                                    file.uploader_name
                                                        ? `Загрузил: ${file.uploader_name}`
                                                        : null
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Card>

                        <Card size="small" title="Участники">
                            <div>
                                Автор:{' '}
                                <b>
                                    {detail.creator_name ||
                                        '—'}
                                </b>
                            </div>
                            <div>
                                Текущий согласующий:{' '}
                                <b>
                                    {detail.assignee_name ||
                                        'не назначен'}
                                </b>
                            </div>
                            <div>
                                Стадия:{' '}
                                <Tag
                                    color={
                                        detail.stage?.color
                                    }
                                >
                                    {detail.stage?.name}
                                </Tag>
                            </div>
                        </Card>

                        {detail.comment && (
                            <Alert
                                type="warning"
                                showIcon
                                message="Комментарий"
                                description={detail.comment}
                            />
                        )}

                        <Card size="small" title="История">
                            <Timeline
                                items={(
                                    detail.actions || []
                                ).map(action => ({
                                    children: (
                                        <div>
                                            <div>
                                                <b>
                                                    {action.user_name}
                                                </b>{' '}
                                                · {action.action}
                                            </div>
                                            <div>
                                                {action.fromStage
                                                    ?.name ||
                                                    '—'}{' '}
                                                →{' '}
                                                {action.toStage
                                                    ?.name ||
                                                    '—'}
                                            </div>
                                            {action.comment && (
                                                <div>
                                                    {
                                                        action.comment
                                                    }
                                                </div>
                                            )}
                                            <Text type="secondary">
                                                {dayjs(
                                                    action.created_at
                                                ).format(
                                                    'DD.MM.YYYY HH:mm'
                                                )}
                                            </Text>
                                        </div>
                                    )
                                }))}
                            />
                        </Card>
                    </Space>
                )}
            </Drawer>

            <Modal
                title="Новое согласование"
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                onOk={handleCreate}
                okText="Создать"
                destroyOnClose
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    initialValues={{
                        priority: 'normal',
                        submit: true
                    }}
                >
                    <Form.Item
                        name="registry_id"
                        label="Документ из реестра (необязательно)"
                    >
                        <Select
                            allowClear
                            showSearch
                            filterOption={false}
                            loading={registrySearching}
                            onSearch={searchRegistries}
                            options={registryOptions}
                            placeholder="Можно не выбирать, если загружаете свои файлы"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Свои файлы"
                        required={false}
                        extra="PDF, Word, Excel, изображения, архивы. Можно несколько."
                    >
                        <Upload
                            multiple
                            beforeUpload={() => false}
                            fileList={createFiles}
                            onChange={({ fileList }) =>
                                setCreateFiles(fileList)
                            }
                        >
                            <Button icon={<UploadOutlined />}>
                                Выбрать файлы
                            </Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item
                        name="assignee_id"
                        label="Согласующий специалист"
                        rules={[
                            {
                                required: true,
                                message: 'Укажите согласующего'
                            }
                        ]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            options={userOptions}
                            placeholder="Кому отправить"
                        />
                    </Form.Item>
                    <Form.Item name="title" label="Название">
                        <Input placeholder="Необязательно" />
                    </Form.Item>
                    <Form.Item
                        name="priority"
                        label="Приоритет"
                    >
                        <Select options={PRIORITY_OPTIONS} />
                    </Form.Item>
                    <Form.Item
                        name="comment"
                        label="Комментарий"
                    >
                        <TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={
                    actionModalTitle[actionType] || 'Действие'
                }
                open={actionOpen}
                onCancel={() => setActionOpen(false)}
                onOk={runAction}
                confirmLoading={actionLoading}
                okText="Выполнить"
                destroyOnClose
            >
                <Form form={actionForm} layout="vertical">
                    {needsNextAssignee && (
                        <Form.Item
                            name="assignee_id"
                            label={
                                actionType === 'approve'
                                    ? 'Руководитель (следующий этап)'
                                    : 'Согласующий'
                            }
                            rules={[
                                {
                                    required: true,
                                    message:
                                        'Выберите сотрудника'
                                }
                            ]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                options={userOptions}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="comment"
                        label="Комментарий"
                        rules={
                            needsComment
                                ? [
                                      {
                                          required: true,
                                          message:
                                              'Обязательный комментарий'
                                      }
                                  ]
                                : []
                        }
                    >
                        <TextArea rows={4} />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default DocWorkflowKanbanPage;
